import { DropIndicator } from "@atlaskit/pragmatic-drag-and-drop-react-drop-indicator/box";
import {
	createOnDropHandler,
	dragAndDropFeature,
	type DragTarget,
	type FeatureImplementation,
	hotkeysCoreFeature,
	insertItemsAtTarget,
	type ItemInstance,
	removeItemsFromParents,
	searchFeature,
	selectionFeature,
	syncDataLoaderFeature,
	type TreeInstance,
} from "@headless-tree/core";
import { useTree } from "@headless-tree/react";
import { filter, moveArray } from "@msuite/utils/array";
import { generateId } from "@msuite/utils/strings";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { BlankSlate } from "../blank-slate";
import { TextInput } from "../form";
import { useSearchForm } from "../hooks";
import { TreeContext } from "./context";
import { DragOverIndicator } from "./drag-over-indicator";
import { TreeBottomSpace } from "./tree-bottom-space";
import { TREE_INDENT, TreeItemRow } from "./tree-item-row";

export { TREE_INDENT };

const customClickBehavior: FeatureImplementation = {
	itemInstance: {
		getProps: ({ prev }) => ({
			...prev?.(),
			onDoubleClick: (_: MouseEvent) => {},
			onClick: (_: MouseEvent) => {},
		}),
	},
};

export type TreeItem<U> = {
	id: string;
	data?: U;
	children: string[]; // a set of ids to fetch the other items
};

export type { ItemInstance, TreeInstance };

/** Props Interface */
interface TreeProps<T extends TreeItem<U>, U> {
	data: Record<string, T>;
	dataLoader: {
		getItem: (itemId: string) => T;
		getChildren: (itemId: string) => string[];
	};
	render: (item: ItemInstance<T>, tree: TreeInstance<T>, api: TreeItemApi<T, U>) => React.ReactNode;
	/**
	 * Called after any structural mutation. `record` is the mutated data record — derive
	 * persisted state from it, NOT from `treeInstance.getItems()`: the instance's flattened
	 * meta is rebuilt asynchronously and can be stale when consumer re-renders interleave
	 * with the (async) drop pipeline.
	 */
	onChange?: (treeInstance: TreeInstance<T>, record: Record<string, T>) => void;
	modifyExternalItem?: (item: Required<T>["data"]) => Required<T>["data"];
	Menu?: (props: TreeExternalApi<T, U>) => React.ReactNode;
}

export interface TreeExternalApi<T, U> {
	/** Append a new item with external data. If parentId is provided, it's appended as last child of that item; otherwise, appended to root. */
	append: (data: U, item?: ItemInstance<T>) => void;
	moveDown: (item: ItemInstance<T>) => void;
	moveUp: (item: ItemInstance<T>) => void;
	unindent: (item: ItemInstance<T>) => void;
}

export interface TreeItemApi<T, U> {
	onRemove: () => void;
	onDuplicate: () => void;
	append: TreeExternalApi<T, U>["append"];
	moveDown: () => void;
	moveUp: () => void;
	unindent: () => void;
	/** Spread onto the element that should act as the sole drag handle for this row. */
	dragHandleProps: Record<string, any>;
}

export function Tree<T extends TreeItem<U>, U>({
	dataLoader,
	data,
	render,
	onChange,
	modifyExternalItem,
	Menu,
}: TreeProps<T, U>) {
	const mountedRef = useRef(true);
	const treeRef = useRef<TreeInstance<T> | null>(null);
	const [, setVersion] = useState(0);
	const [isDraggingOverBlankSlate, setIsDraggingOverBlankSlate] = useState(false);
	const searchForm = useSearchForm("treeSearch");

	// Clean up mounted ref on unmount
	useEffect(() => {
		return () => {
			mountedRef.current = false;
		};
	}, []);

	/** Functions */
	const triggerOnChange = useCallback(() => {
		// Always schedule a local re-render to ensure the tree picks up data changes
		setVersion((v) => v + 1);
		if (onChange && mountedRef.current && treeRef.current) {
			setTimeout(() => {
				if (mountedRef.current && treeRef.current) {
					// Pass the record this render's handlers mutated. Handlers are refreshed
					// into the tree config every render, so an executing handler and its
					// triggerOnChange always close over the same record instance.
					onChange(treeRef.current, data);
				}
			}, 0);
		}
	}, [onChange, data]);

	function insertNewItem(item: T["data"]) {
		try {
			if (!item) throw new Error("No item data");
			const id = generateId();
			data[id] = {
				id,
				data: modifyExternalItem?.(item) || item,
				children: [],
			} as any;
			return id;
		} catch {
			toast.error("Parsing error");
			return null;
		}
	}

	/**
	 * Append a new external data item under the provided parent or root as last child.
	 */
	const append = useCallback(
		(itemData: U, item?: ItemInstance<T>) => {
			const newId = insertNewItem(itemData);
			if (newId) {
				const insertItem = item || treeRef.current?.getRootItem();
				if (insertItem) {
					insertItemsAtTarget(
						[newId],
						{
							item: insertItem,
						},
						(item, newChildrenIds) => {
							const id = item.getId();
							item.expand();
							if (data[id]) {
								data[id].children = newChildrenIds;
							}
						},
					);
				}
				triggerOnChange();
			}
		},
		[data, modifyExternalItem],
	);

	const move = useCallback(
		(item: ItemInstance<T>, direction: "up" | "down") => {
			const parent = item.getParent();
			const offsetIndex = direction === "up" ? -1 : 1;
			const children = parent?.getChildren();
			if (!children) return;
			const childrenIds = children.map((c) => c.getId());
			const currentIndex = childrenIds.indexOf(item.getId());
			const newChildrenIds = moveArray(childrenIds, currentIndex, currentIndex + offsetIndex);
			if (parent) {
				insertItemsAtTarget(
					[],
					{
						item: parent,
					},
					(item) => {
						const id = item.getId();
						item.expand();
						if (data[id]) {
							data[id].children = newChildrenIds;
						}
					},
				).then(() => {
					triggerOnChange();
				});
			}
		},
		[data],
	);

	const unindent = useCallback(
		(item: ItemInstance<T>) => {
			const parent = item.getParent();
			if (!parent) return; // Already at root level
			const newParent = parent.getParent() || tree.getRootItem();
			const itemId = item.getId();
			removeItemsFromParents([item], (it, newChildren) => {
				it.getItemData().children = newChildren;
			}).then(() => {
				insertItemsAtTarget(
					[itemId],
					{
						item: newParent,
					},
					(it, newChildren) => {
						const id = it.getId();
						it.expand();
						if (data[id]) {
							data[id].children = newChildren;
						}
					},
				).then(() => {
					triggerOnChange();
				});
			});
		},
		[data],
	);

	function onDropForeignDragObject(dataTransfer: DataTransfer, target: DragTarget<T>) {
		const item = JSON.parse(dataTransfer.getData("application/json")) as T["data"];
		const newId = insertNewItem(item);
		if (newId) {
			insertItemsAtTarget([newId], target, (item, newChildrenIds) => {
				const id = item.getId();
				item.expand();
				if (data[id]) {
					data[id].children = newChildrenIds;
				}
			});
			triggerOnChange();
		}
	}

	function onCompleteForeignDrop(items: ItemInstance<T>[]) {
		const result = removeItemsFromParents(items, (item, newChildren) => {
			item.getItemData().children = newChildren;
		});
		triggerOnChange();
		return result;
	}

	function onRename(item: ItemInstance<T>, value: string) {
		const id = item.getId();
		if (!data[id]) return;
		data[id].id = value;
		triggerOnChange();
	}

	function canDropForeignDragObject(_: DataTransfer, target: DragTarget<T>) {
		return target.item.isFolder();
	}

	const tree = useTree<T>({
		/** Base Setup */
		rootItemId: "root",
		dataLoader,

		/** Getters */
		getItemName: (item) => item.getItemData().id,
		isItemFolder: (item) => !!item.getItemData().children,

		initialState: {
			expandedItems: Object.keys(data).filter((id) => id !== "root"),
		},

		/** Flags */
		canCheckFolders: false,
		canReorder: true,
		// Restrict native drag start to a dedicated handle element (see TreeItemApi.dragHandleProps)
		// instead of making the whole row draggable.
		seperateDragHandle: true,

		/** Handlers */
		// createOnDropHandler internally runs removeItemsFromParents THEN insertItemsAtTarget,
		// invoking this callback once per step (once for the old parent, once for the new one).
		// Triggering onChange from inside the callback fired onChange twice per drop — the first
		// call reflected the transient "removed from old parent, not yet in new parent" state,
		// which could commit an incomplete/incorrect form snapshot before the second call fixed
		// it. Only trigger it once, after both steps have fully settled.
		onDrop: (items, target) =>
			createOnDropHandler<T>((item, newChildren) => {
				const id = item.getId();
				if (!data[id]) return;
				data[id].children = newChildren;
				item.expand();
			})(items, target).then(() => {
				triggerOnChange();
			}),
		onRename,
		onDropForeignDragObject,
		onCompleteForeignDrop,
		canDropForeignDragObject,

		/** Settings */
		indent: TREE_INDENT,

		/** Features */
		features: [
			syncDataLoaderFeature,
			// dragAndDropFeature's onDragStart reads tree.getSelectedItems() — required or it throws.
			selectionFeature,
			hotkeysCoreFeature,
			dragAndDropFeature,
			customClickBehavior,
			searchFeature,
		],
	});

	treeRef.current = tree;

	/** Effects */
	// The consumer rebuilds the data record from its source of truth whenever that source
	// changes (e.g. a new form snapshot). The tree instance's flattened item meta is only
	// rebuilt by the drag/insert utilities against whatever record the config referenced at
	// that moment — which can be an older instance when re-renders interleave with the async
	// drop pipeline. Explicitly re-flatten against the current record whenever it changes so
	// the rendered tree always reflects it.
	//
	// expandedItems is only seeded from initialState on mount, so an id that enters the
	// record afterwards (e.g. a duplicated block) would stay collapsed and its children
	// would never be flattened into the visible items — with no expand control to recover.
	// Expand every id we haven't seen before, before re-flattening.
	const knownIdsRef = useRef<Set<string> | null>(null);
	useEffect(() => {
		if (knownIdsRef.current === null) {
			knownIdsRef.current = new Set(Object.keys(data));
		} else {
			const knownIds = knownIdsRef.current;
			const newIds: string[] = [];
			for (const id of Object.keys(data)) {
				if (knownIds.has(id)) continue;
				knownIds.add(id);
				newIds.push(id);
			}
			if (newIds.length) {
				tree.applySubStateUpdate("expandedItems", (prev) => [...(prev ?? []), ...newIds]);
			}
		}
		tree.rebuildTree();
	}, [data, tree]);

	/** Computed */
	const visibleItems: ItemInstance<T>[] = [];
	for (const item of tree.getItems()) {
		if (searchForm.searchValue && !filter([item.getItemData()], searchForm.searchValue)?.length) {
			continue;
		}
		visibleItems.push(item);
	}

	/** Render */
	return (
		<TreeContext.Provider value={{ append: append as any }}>
			<div className="h-stack justify-between mb-2 p-1">
				<div {...searchForm.getSearchInputWrapperProps()}>
					<TextInput {...searchForm.getSearchInputProps()} />
				</div>
				{Menu?.({
					append,
					moveDown: (item) => move(item, "down"),
					moveUp: (item) => move(item, "up"),
					unindent,
				})}
			</div>
			<div
				{...tree.getContainerProps()}
				className="v-stack relative h-full flex-grow-0 overflow-hidden"
			>
				{tree.getItems()?.length === 0 ? (
					<div
						className="h-full flex flex-col relative"
						onDragEnter={() => setIsDraggingOverBlankSlate(true)}
						onDragLeave={() => setIsDraggingOverBlankSlate(false)}
						onDrop={() =>
							createOnDropHandler((item, newChildren) => {
								setIsDraggingOverBlankSlate(false);
								const id = item.getId();
								if (!data[id]) return;
								data[id].children = newChildren;
								item.expand();
								triggerOnChange();
							})
						}
					>
						<BlankSlate />
						{isDraggingOverBlankSlate && (
							<div className="absolute inset-0 z-10 pointer-events-none">
								<DragOverIndicator />
							</div>
						)}
					</div>
				) : (
					<div className="v-stack overflow-y-auto h-full flex-grow-0">
						{visibleItems.map((item, index) => (
							<div
								key={item.getId()}
								className={index === 0 ? undefined : "pt-2.5"}
							>
								<TreeItemRow
									item={item}
									tree={tree}
									data={data}
									render={render}
									append={append}
									move={move}
									unindent={unindent}
									triggerOnChange={triggerOnChange}
								/>
							</div>
						))}
						<TreeBottomSpace />
					</div>
				)}
				<div style={tree.getDragLineStyle(-3, 4)}>
					<DropIndicator edge="top" />
				</div>
			</div>
		</TreeContext.Provider>
	);
}
